import type { CaptureDiagnostic } from '../audio/captureDiagnostics';
import type { TakeStyle } from '../audio/prompts';
import type { TakeStats } from '../audio/recorder';
import { requestJson } from './http';
import type { DonorSessionView } from './voiceInvitationsApi';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

const cookieRequest: RequestInit = { credentials: 'include' };
const jsonHeaders = { 'Content-Type': 'application/json' };

export type GuestCaptureStage =
  | 'details'
  | 'consent'
  | 'voice_samples'
  | 'listener_clips'
  | 'complete'
  | 'building'
  | 'ready'
  | 'blocked';

export type GuestBuildStatus =
  | 'pending'
  | 'queue_pending'
  | 'building'
  | 'ready'
  | 'failed'
  | 'blocked';

export interface GuestCaptureListenerProgress {
  invitation_listener_id: string;
  display_name: string;
  persisted_clip_keys: Array<'intro' | 'name' | 'goodnight'>;
  complete: boolean;
}

export interface GuestCaptureProgressResponse {
  version: 1;
  stage: GuestCaptureStage;
  voice_samples: {
    expected: number;
    persisted_indexes: number[];
    complete: boolean;
  };
  listeners: GuestCaptureListenerProgress[];
  durable_capture_complete: boolean;
  build_status: GuestBuildStatus;
  draft_scope_key: string;
}

export interface GuestCaptureQualityReport {
  duration_seconds: number;
  loudness_db: number;
  signal_quality?: string | null;
  clipping?: string | null;
  clipping_percent?: number | null;
  silence_percent?: number | null;
  snr_db?: number | null;
  sample_rate?: number | null;
}

export interface GuestCaptureUploadResponse {
  accepted: boolean;
  sample_index: number;
  quality_report: GuestCaptureQualityReport;
  rejection_reasons?: string[] | null;
  suggestions?: string[] | null;
  message: string;
  storage_confirmed: boolean;
}

export interface GuestCaptureCompletionResponse {
  state: 'completed';
  durable_capture_complete: true;
  build_status: Exclude<GuestBuildStatus, 'pending'>;
}

export interface GuestConsentDecisions {
  voice_model_creation: 'granted';
  household_voice_share: 'granted';
  terms: 'acknowledged';
  privacy: 'acknowledged';
}

export function fetchDonorSession(): Promise<DonorSessionView> {
  return requestJson<DonorSessionView>(`${API_BASE_URL}/donor-session`, cookieRequest);
}

export function acceptDonorSession(details: {
  donorDisplayName: string;
  relationshipLabel: string | null;
  voiceLabel: string;
}): Promise<{ state: 'accepted'; voice_label: string }> {
  return requestJson(`${API_BASE_URL}/donor-session/accept`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify({
      donor_display_name: details.donorDisplayName,
      relationship_label: details.relationshipLabel,
      voice_label: details.voiceLabel,
    }),
  });
}

export function consentDonorSession(
  decisions: GuestConsentDecisions,
): Promise<{ state: 'consented' | 'revoked'; consent_event_count: number }> {
  return requestJson(`${API_BASE_URL}/donor-session/consent`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify({ decisions }),
  });
}

export function fetchDonorCaptureProgress(): Promise<GuestCaptureProgressResponse> {
  return requestJson(`${API_BASE_URL}/donor-session/capture`, cookieRequest);
}

export function completeDonorCapture(): Promise<GuestCaptureCompletionResponse> {
  return requestJson(`${API_BASE_URL}/donor-session/capture/complete`, {
    method: 'POST',
    credentials: 'include',
  });
}

export function uploadDonorCaptureSample(params: {
  sampleIndex: number;
  style: TakeStyle;
  stats: TakeStats;
  blob: Blob;
  forced?: boolean;
}): Promise<GuestCaptureUploadResponse> {
  const form = new FormData();
  form.append('sample_index', String(params.sampleIndex));
  form.append('style', params.style);
  form.append('forced', String(params.forced ?? false));
  form.append('dsp_suppressed', String(params.stats.dspSuppressed));
  form.append(
    'file',
    new File([params.blob], `sample_${params.sampleIndex}.wav`, {
      type: 'audio/wav',
    }),
  );
  return requestJson(`${API_BASE_URL}/donor-session/capture/samples`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
}

export function uploadDonorListenerClip(params: {
  invitationListenerId: string;
  clipKey: 'intro' | 'name' | 'goodnight';
  blob: Blob;
  forced?: boolean;
}): Promise<GuestCaptureUploadResponse> {
  const form = new FormData();
  form.append('clip_key', params.clipKey);
  form.append('forced', String(params.forced ?? false));
  form.append(
    'file',
    new File([params.blob], `${params.clipKey}.wav`, { type: 'audio/wav' }),
  );
  return requestJson(
    `${API_BASE_URL}/donor-session/listeners/${encodeURIComponent(params.invitationListenerId)}/clips`,
    {
      method: 'POST',
      credentials: 'include',
      body: form,
    },
  );
}

function postEmpty(path: string): Promise<{ status: 'revoked' }> {
  return requestJson(`${API_BASE_URL}/donor-session${path}`, {
    method: 'POST',
    credentials: 'include',
  });
}

export function revokeDonorSessionShare(): Promise<{ status: 'revoked' }> {
  return postEmpty('/revoke-share');
}

export function revokeDonorSessionVoice(): Promise<{ status: 'revoked' }> {
  return postEmpty('/revoke-voice');
}

export function requestDonorSessionResumeLink(): Promise<{ status: string }> {
  return requestJson<{ status: string }>(`${API_BASE_URL}/donor-session/resume-link`, {
    method: 'POST',
    credentials: 'include',
  });
}

export function declineDonorSession(): Promise<{ state: string }> {
  return requestJson<{ state: string }>(`${API_BASE_URL}/donor-session/decline`, {
    method: 'POST',
    credentials: 'include',
  });
}

/** Guest diagnostics use the HttpOnly donor session, never an invitation token. */
export async function emitDonorCaptureDiagnostic(event: CaptureDiagnostic): Promise<void> {
  try {
    await requestJson(`${API_BASE_URL}/donor-session/events`, {
      method: 'POST',
      credentials: 'include',
      headers: jsonHeaders,
      body: JSON.stringify(event),
    });
  } catch { /* A missing diagnostic must never block or reset capture. */ }
}
